import { NavLink, useNavigate } from 'react-router-dom';
import logo from '../../assets/teamly-light.png';
import logoSmall from '../../assets/teamly-small.svg';
import GlobalSearch from './GlobalSearch';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  Building2,
  LayoutDashboard,
  LogOut,
  Settings,
  ChevronLeft,
  ChevronRight,
  Globe,
} from 'lucide-react';
import { useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';

const SuperAdminSidebar = ({ mobile, onMobileClose, collapsed, setCollapsed }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/superadmin/login');
  };

  const navItems = [
    {
      title: 'Companies',
      href: '/superadmin/companies',
      icon: Building2,
    },
    {
      title: 'Professional Tax Slabs',
      href: '/superadmin/pt-settings',
      icon: Globe,
    },
    {
      title: 'System Settings',
      href: '/superadmin/settings',
      icon: Settings,
    },
  ];

  const isCollapsed = collapsed && !mobile;

  return (
    <aside
      className={cn(
        'flex flex-col h-full transition-all duration-300 relative',
        mobile ? 'w-full px-2' : (isCollapsed ? 'w-full' : 'w-full'),
        !mobile && 'z-50 h-screen border-r border-white/5 bg-[#0a0a0a] hidden lg:block'
      )}
    >
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="flex h-16 items-center justify-between border-b border-white/5 px-4 bg-black/20 backdrop-blur-sm">
          {!isCollapsed && (
            <div className="flex items-center">
              <img src={logo} alt="Logo" className="h-7 w-auto object-contain" />
            </div>
          )}
          {isCollapsed && (
            <div className="flex justify-center w-full">
              <img src={logoSmall} alt="Logo" className="h-8 w-8 object-contain" />
            </div>
          )}
        </div>


        {/* Desktop Toggle Button - Vertical Pill Tab */}
        {!mobile && (
          <button

            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              "absolute left-full top-3 h-10 w-2 bg-amber-400 text-black hover:bg-amber-500 border-none z-[100] transition-all duration-300",
              "flex items-center justify-center p-0 cursor-pointer outline-none ring-0 rounded-r-lg"
            )}
            style={{
              // Your custom inline styles go here
              marginLeft: '1px',
            }}
            data-testid="sidebar-toggle"
          >
            {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        )}

        {/* Navigation */}
        <ScrollArea className="flex-1 px-3 py-4">
          <GlobalSearch collapsed={isCollapsed} />
          <nav className="space-y-1">
            {navItems.map((item) => {
              const NavItem = (
                <NavLink
                  key={item.href}
                  to={item.href}
                  onClick={() => onMobileClose && onMobileClose()}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg font-medium transition-all duration-200',
                      !isCollapsed && 'px-3 py-2.5 text-sm',
                      isCollapsed && 'h-10 w-10 mx-auto justify-center',
                      isActive
                        ? 'bg-amber-400 text-black shadow-sm font-bold'
                        : 'text-slate-400 hover:bg-white/5 hover:text-white'
                    )
                  }
                  data-testid={`nav-${item.title.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!isCollapsed && <span>{item.title}</span>}
                </NavLink>
              );

              if (isCollapsed) {
                return (
                  <Tooltip key={item.href} delayDuration={0}>
                    <TooltipTrigger asChild>
                      <div className="flex justify-center w-full py-0.5">
                        {NavItem}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={10} className="font-semibold bg-slate-900 border-white/10 text-white">
                      {item.title}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return NavItem;
            })}
          </nav>
        </ScrollArea>

        {/* User section */}
        <div className="border-t border-white/5 p-3 bg-white/[0.02]">
          {!isCollapsed && (
            <div className="mb-3 px-3">
              <p className="text-sm font-medium text-white truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          )}
          {isCollapsed ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-center px-2 text-slate-400 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10} className="font-medium text-red-500 bg-slate-900 border-white/10">
                Logout
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-400/10 border border-transparent hover:border-red-400/20"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              <span className="ml-3">Logout</span>
            </Button>
          )}
        </div>
      </div>
    </aside>
  );
};

export default SuperAdminSidebar;
